import type { FieldDefinition, Filter, FilterCondition } from "@estelle/domain";
import { Layers, Plus, Search } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { AdvancedFilterEditor } from "@/components/filters/AdvancedFilterEditor";
import { groupFields } from "@/components/filters/common";
import { ConditionEditor } from "@/components/filters/ConditionEditor";
import { newConditionFor } from "@/components/filters/defaults";
import { addCondition } from "@/components/filters/tree";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type Step =
  | { kind: "pick" }
  | { kind: "condition"; condition: FilterCondition }
  | { kind: "group" };

/**
 * "+ Filter" (#633): the way in to filtering that people can see.
 *
 * Filtering used to start by typing in the search box and picking a field from
 * what dropped down, and nobody found it. This is a button: a searchable list
 * of every field the table supports, Common first, then More, and "Add a
 * group" for the AND/OR builder. Picking a field opens its value picker in the
 * same popover; Add ANDs the condition with what is there.
 */
export function AddFilter({
  fields,
  common,
  value,
  onChangeFilter,
  className,
}: {
  fields: FieldDefinition[];
  common: readonly FieldDefinition[];
  value: Filter;
  onChangeFilter: (next: Filter) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>({ kind: "pick" });
  const [query, setQuery] = useState("");

  const groups = useMemo(() => groupFields(fields, common), [fields, common]);
  const text = query.trim().toLowerCase();
  const matches = (field: FieldDefinition) =>
    text === "" || field.label.toLowerCase().includes(text);

  function close() {
    setOpen(false);
    setStep({ kind: "pick" });
    setQuery("");
  }

  function section(title: string, list: FieldDefinition[]): ReactNode {
    const shown = list.filter(matches);
    if (shown.length === 0) return null;
    return (
      <div>
        <p className="text-muted-foreground px-2 pt-2 pb-1 text-[11px] font-semibold tracking-wide uppercase">
          {title}
        </p>
        {shown.map((field) => (
          <button
            key={field.name}
            type="button"
            onClick={() => {
              setStep({ kind: "condition", condition: newConditionFor(field) });
            }}
            className="hover:bg-accent focus-visible:bg-accent flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none"
          >
            {field.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) setOpen(true);
        else close();
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={cn("shrink-0 gap-1 border-dashed", className)}
        >
          <Plus aria-hidden />
          Filter
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn("p-0", step.kind === "pick" ? "w-72" : "w-auto p-3")}
      >
        {step.kind === "pick" ? (
          <div className="flex max-h-96 flex-col">
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Search
                aria-hidden
                className="text-muted-foreground size-4 shrink-0"
              />
              <input
                autoFocus
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                placeholder="Filter by…"
                aria-label="Find a field to filter by"
                className="h-7 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-1">
              {section("Common", groups.common)}
              {section(
                groups.common.length > 0 ? "More" : "Fields",
                groups.more,
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setStep({ kind: "group" });
              }}
              className="hover:bg-accent text-muted-foreground flex items-center gap-2 border-t px-3 py-2 text-left text-sm"
            >
              <Layers aria-hidden className="size-4" />
              Add a group (AND / OR)…
            </button>
          </div>
        ) : step.kind === "condition" ? (
          <ConditionEditor
            condition={step.condition}
            fields={fields}
            submitLabel="Apply"
            onSubmit={(condition) => {
              onChangeFilter(addCondition(value, condition));
              close();
            }}
            onRemove={() => {
              setStep({ kind: "pick" });
            }}
          />
        ) : (
          <AdvancedFilterEditor
            value={value}
            fields={fields}
            onApply={onChangeFilter}
            onClose={close}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
